import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const LanguageContext = createContext();

const translations = {
  en: {
    nav: {
      products: "Products",
      operations: "Operations",
      markets: "Markets",
      logistics: "Logistics",
      certifications: "Certifications",
      requestQuote: "Request a Quote"
    },
    hero: {
      subtitle: "DEDARI BALI EXPORT",
      headline: "Handcrafted Bali Ata Bags & Spices — Direct from the Source",
      description: "We are a founder-led export house in Bali, connecting international buyers directly with artisan communities and highland farms. Pilot orders welcome. No middlemen, no inflated costs.",
      cta: "Request a Quote",
      ctaSecondary: "View Products"
    },
    products: {
      sectionLabel: "PRODUCT CATALOG",
      title: "What We Export",
      tabBags: "Handcrafted Ata Bags",
      tabSpices: "Spices & Herbs",
      bagsIntro: "Each Ata bag is hand-woven by skilled artisans in Bali using centuries-old techniques. The natural Ata grass is dried, woven, and smoked over coconut husks for 2-3 days, giving each piece its signature warm tone and exceptional durability. We work directly with artisan groups — no subcontracting.",
      spicesIntro: "We source raw and dried spices directly from Indonesian highland farm partners. All products undergo quality inspection before packing.",
      material: "Material",
      weavingTime: "Weaving Time",
      finishing: "Finishing",
      lining: "Lining",
      closure: "Closure",
      moq: "MOQ",
      leadTime: "Lead Time",
      moistureContent: "Moisture Content",
      dryingMethod: "Drying Method",
      cutSize: "Cut Size",
      shelfLife: "Shelf Life",
      packaging: "Packaging",
      origin: "Origin",
      priceIndication: "Price Indication",
      certifications: "Certifications"
    },
    operations: {
      sectionLabel: "HOW WE WORK",
      title: "Direct Sourcing, No Middlemen",
      qcTitle: "Our Quality Process",
      qcItems: [
        "Direct visit to artisan workshops — we know every maker by name",
        "Visual and dimensional inspection on every batch before packing",
        "Spices: moisture and contamination check before sealing",
        "Pre-shipment photos and packing list shared with buyer",
        "Full traceability — farm/village name available on request"
      ],
      capacityTitle: "Current Capacity",
      capacityItems: [
        { label: "Ata Bags", value: "300–500 pcs / month (pilot)" },
        { label: "Dried Spices", value: "On request — farm partners" },
        { label: "Lead Time", value: "30–45 days from order" },
        { label: "Min. Order", value: "75–150 pcs (varies by SKU)" }
      ]
    },
    markets: {
      sectionLabel: "TARGET MARKETS",
      title: "Where We Are Focused",
      regions: [
        {
          name: "United States",
          countries: "Primary pilot market",
          products: "Handwoven Ata Bags",
          highlight: "Currently accepting pilot orders"
        },
        {
          name: "Europe",
          countries: "Germany, Netherlands, UK",
          products: "Ata Bags, Spices",
          highlight: "Open to enquiries — EU compliance docs in progress"
        },
        {
          name: "Australia",
          countries: "Australia, New Zealand",
          products: "Ata Bags, Dried Spices",
          highlight: "Open to enquiries — phytosanitary available"
        }
      ]
    },
    logistics: {
      sectionLabel: "LOGISTICS & PACKAGING",
      title: "Shipping Terms & Standards",
      subtitle: "We handle the complexity of international shipping so you don't have to. All orders are packaged to international export standards and shipped from major Indonesian ports.",
      incoterms: [
        {
          code: "FOB",
          name: "Free on Board",
          description: "Goods delivered to the port of origin. Buyer arranges sea freight and insurance. Most common for bulk spice orders.",
          ports: "Semarang, Surabaya, Jakarta"
        },
        {
          code: "CIF",
          name: "Cost, Insurance & Freight",
          description: "We handle freight and insurance to your destination port. Ideal for first-time buyers who want a turnkey solution.",
          ports: "Worldwide major ports"
        },
        {
          code: "EXW",
          name: "Ex Works",
          description: "Goods available at our warehouse in Bali. Buyer handles all logistics from pickup. Best for buyers with existing freight forwarders in Indonesia.",
          ports: "Denpasar, Bali"
        }
      ],
      packagingTitle: "Packaging Standards",
      packagingItems: [
        "Spices: 25 kg PP woven bags with vacuum-sealed inner lining",
        "Ata Bags: Individual bubble wrap + corrugated carton boxes",
        "Palletized for container loading (20ft / 40ft FCL available)",
        "Custom labeling and branding available for private-label buyers",
        "Fumigation certificates provided for all shipments"
      ]
    },
    certifications: {
      sectionLabel: "DOCUMENTS & COMPLIANCE",
      title: "Export Documents We Provide",
      subtitle: "We prepare the standard export documentation required for your shipment. Additional certifications are available on request depending on your destination market.",
      certs: [
        { name: "Phytosanitary Certificate", description: "Government-issued plant health certificate — available for all agricultural exports" },
        { name: "Certificate of Origin", description: "Issued by the Indonesian Chamber of Commerce (Kadin)" },
        { name: "Commercial Invoice & Packing List", description: "ICC-standard documents provided for every shipment" },
        { name: "Halal MUI", description: "In process — available for select spice products on request" },
        { name: "HACCP", description: "In process — our spice suppliers are working toward certification" },
        { name: "SVLK (Timber Legality)", description: "Available via certified export partners for rattan products" }
      ]
    },
    quote: {
      sectionLabel: "GET IN TOUCH",
      title: "Request a Quote",
      subtitle: "Fill in the form below and our export team will get back to you within 24 business hours with pricing and availability.",
      fullName: "Full Name",
      companyName: "Company Name",
      email: "Email Address",
      whatsapp: "WhatsApp / Phone",
      country: "Country of Destination",
      productInterest: "Product Interest",
      estimatedQuantity: "Estimated Quantity",
      incoterms: "Preferred Incoterms",
      message: "Additional Notes",
      submit: "Submit Quote Request",
      submitting: "Submitting...",
      success: "Quote request submitted successfully! We will contact you within 24 hours.",
      error: "Failed to submit. Please try again or contact us directly.",
      selectProduct: "Select a product",
      selectIncoterms: "Select incoterms",
      productOptions: [
        "Ata Bags - Classic Round",
        "Ata Bags - Rectangular Clutch",
        "Ata Bags - Tote Basket",
        "Dried Lemongrass (Sereh)",
        "Ginger (Jahe) - Dried",
        "Ginger (Jahe) - Fresh",
        "Turmeric (Kunyit)",
        "Multiple Products"
      ]
    },
    footer: {
      tagline: "Connecting Indonesia's finest products with the world. Quality, reliability, and trust in every shipment.",
      quickLinks: "Quick Links",
      exportInfo: "Export Information",
      contactUs: "Contact Us",
      shippingTerms: "Shipping Terms",
      qualityAssurance: "Quality Assurance",
      certificates: "Certificates",
      rights: "All rights reserved.",
      madeIn: "Made with dedication in Bali, Indonesia"
    }
  },
  id: {
    nav: {
      products: "Produk",
      operations: "Operasi",
      markets: "Pasar",
      logistics: "Logistik",
      certifications: "Sertifikasi",
      requestQuote: "Minta Penawaran"
    },
    hero: {
      subtitle: "DEDARI BALI EXPORT",
      headline: "Tas Ata & Rempah Bali Asli — Langsung dari Sumbernya",
      description: "Kami adalah rumah ekspor yang dipimpin langsung oleh founder di Bali, menghubungkan pembeli internasional dengan komunitas pengrajin dan petani lokal. Pilot order disambut. Tanpa perantara, tanpa biaya yang membengkak.",
      cta: "Minta Penawaran",
      ctaSecondary: "Lihat Produk"
    },
    products: {
      sectionLabel: "KATALOG PRODUK",
      title: "Apa yang Kami Ekspor",
      tabBags: "Tas Ata Kerajinan Tangan",
      tabSpices: "Rempah & Herbal",
      bagsIntro: "Setiap tas Ata dianyam tangan oleh pengrajin terampil di Bali menggunakan teknik berusia berabad-abad. Rumput Ata alami dikeringkan, dianyam, dan diasap di atas tempurung kelapa selama 2-3 hari. Kami bekerja langsung dengan kelompok pengrajin — tanpa subkontrak.",
      spicesIntro: "Kami sumber rempah mentah dan kering langsung dari mitra petani dataran tinggi Indonesia. Semua produk melalui inspeksi kualitas sebelum dikemas.",
      material: "Bahan",
      weavingTime: "Waktu Anyam",
      finishing: "Penyelesaian",
      lining: "Lapisan",
      closure: "Penutup",
      moq: "MOQ",
      leadTime: "Waktu Produksi",
      moistureContent: "Kadar Air",
      dryingMethod: "Metode Pengeringan",
      cutSize: "Ukuran Potongan",
      shelfLife: "Masa Simpan",
      packaging: "Kemasan",
      origin: "Asal",
      priceIndication: "Indikasi Harga",
      certifications: "Sertifikasi"
    },
    operations: {
      sectionLabel: "CARA KAMI BEKERJA",
      title: "Sumber Langsung, Tanpa Perantara",
      qcTitle: "Proses Kualitas Kami",
      qcItems: [
        "Kunjungan langsung ke bengkel pengrajin — kami kenal setiap pembuat",
        "Inspeksi visual dan dimensi pada setiap batch sebelum dikemas",
        "Rempah: cek kadar air dan kontaminasi sebelum disegel",
        "Foto pra-pengiriman dan packing list dibagikan ke buyer",
        "Ketertelusuran penuh — nama desa/pertanian tersedia atas permintaan"
      ],
      capacityTitle: "Kapasitas Saat Ini",
      capacityItems: [
        { label: "Tas Ata", value: "300–500 pcs / bulan (pilot)" },
        { label: "Rempah Kering", value: "Atas permintaan — mitra petani" },
        { label: "Lead Time", value: "30–45 hari dari pesanan" },
        { label: "Min. Order", value: "75–150 pcs (tergantung SKU)" }
      ]
    },
    markets: {
      sectionLabel: "PASAR TARGET",
      title: "Di Mana Kami Fokus",
      regions: [
        {
          name: "Amerika Serikat",
          countries: "Pasar pilot utama",
          products: "Tas Ata Anyaman Tangan",
          highlight: "Saat ini menerima pilot order"
        },
        {
          name: "Eropa",
          countries: "Jerman, Belanda, Inggris",
          products: "Tas Ata, Rempah",
          highlight: "Terbuka untuk pertanyaan — dokumen EU sedang diproses"
        },
        {
          name: "Australia",
          countries: "Australia, Selandia Baru",
          products: "Tas Ata, Rempah Kering",
          highlight: "Terbuka untuk pertanyaan — phytosanitary tersedia"
        }
      ]
    },
    logistics: {
      sectionLabel: "LOGISTIK & KEMASAN",
      title: "Ketentuan Pengiriman & Standar",
      subtitle: "Kami menangani kompleksitas pengiriman internasional. Semua pesanan dikemas sesuai standar ekspor internasional dan dikirim dari pelabuhan utama Indonesia.",
      incoterms: [
        {
          code: "FOB",
          name: "Free on Board",
          description: "Barang dikirim ke pelabuhan asal. Pembeli mengatur pengiriman laut dan asuransi. Paling umum untuk pesanan rempah curah.",
          ports: "Semarang, Surabaya, Jakarta"
        },
        {
          code: "CIF",
          name: "Cost, Insurance & Freight",
          description: "Kami menangani pengiriman dan asuransi ke pelabuhan tujuan Anda. Ideal untuk pembeli pertama kali.",
          ports: "Pelabuhan utama seluruh dunia"
        },
        {
          code: "EXW",
          name: "Ex Works",
          description: "Barang tersedia di gudang kami di Bali. Pembeli menangani semua logistik dari pengambilan.",
          ports: "Denpasar, Bali"
        }
      ],
      packagingTitle: "Standar Kemasan",
      packagingItems: [
        "Rempah: Karung PP 25 kg dengan lapisan dalam vakum",
        "Tas Ata: Bubble wrap individual + kotak karton bergelombang",
        "Dipaletkan untuk pemuatan kontainer (20ft / 40ft FCL tersedia)",
        "Pelabelan dan branding kustom tersedia untuk pembeli private-label",
        "Sertifikat fumigasi disediakan untuk semua pengiriman"
      ]
    },
    certifications: {
      sectionLabel: "DOKUMEN & KEPATUHAN",
      title: "Dokumen Ekspor yang Kami Sediakan",
      subtitle: "Kami menyiapkan dokumentasi ekspor standar yang dibutuhkan untuk pengiriman Anda. Sertifikasi tambahan tersedia atas permintaan sesuai pasar tujuan.",
      certs: [
        { name: "Sertifikat Fitosanitari", description: "Sertifikat kesehatan tanaman dari pemerintah — tersedia untuk semua ekspor pertanian" },
        { name: "Surat Keterangan Asal", description: "Diterbitkan oleh Kamar Dagang Indonesia (Kadin)" },
        { name: "Commercial Invoice & Packing List", description: "Dokumen standar ICC disediakan untuk setiap pengiriman" },
        { name: "Halal MUI", description: "Dalam proses — tersedia untuk produk rempah pilihan atas permintaan" },
        { name: "HACCP", description: "Dalam proses — pemasok rempah kami sedang menuju sertifikasi" },
        { name: "SVLK (Legalitas Kayu)", description: "Tersedia melalui mitra ekspor bersertifikat untuk produk rotan" }
      ]
    },
    quote: {
      sectionLabel: "HUBUNGI KAMI",
      title: "Minta Penawaran",
      subtitle: "Isi formulir di bawah dan tim ekspor kami akan menghubungi Anda dalam 24 jam kerja dengan harga dan ketersediaan.",
      fullName: "Nama Lengkap",
      companyName: "Nama Perusahaan",
      email: "Alamat Email",
      whatsapp: "WhatsApp / Telepon",
      country: "Negara Tujuan",
      productInterest: "Produk yang Diminati",
      estimatedQuantity: "Estimasi Jumlah",
      incoterms: "Incoterms Pilihan",
      message: "Catatan Tambahan",
      submit: "Kirim Permintaan Penawaran",
      submitting: "Mengirim...",
      success: "Permintaan penawaran berhasil dikirim! Kami akan menghubungi Anda dalam 24 jam.",
      error: "Gagal mengirim. Silakan coba lagi atau hubungi kami langsung.",
      selectProduct: "Pilih produk",
      selectIncoterms: "Pilih incoterms",
      productOptions: [
        "Tas Ata - Bulat Klasik",
        "Tas Ata - Clutch Persegi",
        "Tas Ata - Tote Basket",
        "Serai Kering (Sereh)",
        "Jahe (Jahe) - Kering",
        "Jahe (Jahe) - Segar",
        "Kunyit (Kunyit)",
        "Beberapa Produk"
      ]
    },
    footer: {
      tagline: "Menghubungkan produk terbaik Indonesia dengan dunia. Kualitas, keandalan, dan kepercayaan di setiap pengiriman.",
      quickLinks: "Tautan Cepat",
      exportInfo: "Informasi Ekspor",
      contactUs: "Hubungi Kami",
      shippingTerms: "Ketentuan Pengiriman",
      qualityAssurance: "Jaminan Kualitas",
      certificates: "Sertifikat",
      rights: "Hak cipta dilindungi.",
      madeIn: "Dibuat dengan dedikasi di Bali, Indonesia"
    }
  }
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(
    () => localStorage.getItem('dedari_lang') || 'en'
  );

  const setLanguageAndPersist = useCallback((lang) => {
    localStorage.setItem('dedari_lang', lang);
    setLanguage(lang);
  }, []);

  const t = useCallback((path) => {
    const keys = path.split('.');
    let result = translations[language];
    for (const key of keys) {
      if (result && result[key] !== undefined) result = result[key];
      else return path;
    }
    return result;
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage: setLanguageAndPersist, t }), [language, setLanguageAndPersist, t]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
